// src/pages/HrDashboard.js
import { Toaster } from 'react-hot-toast';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { authApi, surveyApi, mentoringApi as mApi } from '../axiosInstance';

import CurrentSurveysTile from './HrDashboard/CurrentSurveysTile';
import UnregisteredInvitationsTile from './HrDashboard/UnregisteredInvitationsTile';
import MatchingTile from './HrDashboard/MatchingTile';
import MentoringInviteTile from './HrDashboard/MentoringInviteTile';
import MentoringPairsTile from './HrDashboard/MentoringPairsTile';
import MissingParticipants from '../components/MissingParticipants';
import HrActionsTile from './HrDashboard/HrActionsTile';

// NEU: Session-Helper (wie bei Mentees)
import { ensureMentoringSession } from '../lib/session';
import { getHrProjectFilterId, setHrProjectFilterId } from '../lib/hrProjectFilter';

/* -------- Helpers -------- */
function extractCustomerIdFromWhoami(who) {
  if (!who) return null;
  const src = who.data?.user ?? who.user ?? who.profile ?? who.hr_profile ?? who;
  const candidates = [
    src?.customer_id,
    src?.customerId,
    src?.tenant_id,
    src?.tenantId,
    src?.customer?.id,
    src?.tenant?.id,
  ];
  for (const v of candidates) {
    const n = Number(v);
    if (Number.isInteger(n) && n > 0) return n;
  }
  return null;
}

// DEBUG optional: localStorage.DEBUG_HR = 'true'
function dbg(...args) {
  if (localStorage.DEBUG_HR === 'true') console.log('[HR-DEBUG]', ...args);
}

/**
 * Mentoring-first User-Ermittlung:
 * 1) ensureMentoringSession()
 * 2) /mentoring/whoami
 * 3) Fallback: /mentoring/tenant-package (+ sso-attach)
 * 4) Letzter Fallback: /auth/profile /auth/whoami (falls dein Auth später aktiv ist)
 */
async function fetchUserEffective() {
  // 1) Session im Mentoring sicherstellen
  try {
    await ensureMentoringSession();
  } catch (e) {
    dbg('ensureMentoringSession error', e);
  }

  // 2) Mentoring whoami
  try {
    const { data } = await mApi.get('/whoami', {
      withCredentials: true,
      timeout: 8000,
      validateStatus: (s) => s >= 200 && s < 500,
    });
    const u = data?.user || {};
    const cid = u.customer_id ?? u.client_id ?? null;
    if (cid) {
      dbg('whoami (mentoring) user', u);
      return {
        ...u,
        customer_id: cid,
        role: u.role ?? 'hr',
        email: u.email ?? null,
        first_name: u.first_name ?? u.firstName ?? null,
        last_name: u.last_name ?? u.lastName ?? null,
      };
    }
  } catch (e) {
    dbg('mentoring whoami error', e);
  }

  // 3) Fallback: tenant-package → notfalls sso-attach + minimaler User
  try {
    const tp = await mApi
      .get('/tenant-package', { withCredentials: true, timeout: 8000 })
      .then((r) => r.data);
    dbg('tenant-package', tp);
    if (tp?.ok && tp.customer_id && tp.email) {
      // versuchen, saubere Session zu setzen (no-op wenn schon vorhanden)
      try {
        await mApi.post(
          '/sso-attach',
          { user: { email: tp.email, role: 'hr', customer_id: tp.customer_id, client_id: tp.customer_id } },
          { withCredentials: true }
        );
        // danach nochmal whoami versuchen
        try {
          const { data } = await mApi.get('/whoami', { withCredentials: true, timeout: 6000 });
          const u = data?.user || {};
          const cid = u.customer_id ?? u.client_id ?? tp.customer_id;
          if (cid) {
            return {
              ...u,
              customer_id: cid,
              role: u.role ?? 'hr',
              email: u.email ?? tp.email,
              first_name: u.first_name ?? u.firstName ?? null,
              last_name: u.last_name ?? u.lastName ?? null,
            };
          }
        } catch {}
      } catch (e) {
        dbg('sso-attach via tenant-package failed (non-fatal)', e);
      }

      // Minimaler Fallback-User (ohne whoami)
      return {
        customer_id: Number(tp.customer_id),
        role: 'hr',
        email: tp.email,
        first_name: null,
        last_name: null,
      };
    }
  } catch (e) {
    dbg('tenant-package error', e);
  }

  // 4) Auth (nur wenn wirklich authenticated)
  try {
    const { data } = await authApi.get('/profile', {
      withCredentials: true,
      timeout: 8000,
      validateStatus: (s) => s >= 200 && s < 500,
    });
    if (data?.isAuthenticated && (data.customer_id || data.customerId)) {
      return {
        ...data,
        customer_id: data.customer_id ?? data.customerId ?? null,
        role: data.role ?? data.user?.role ?? null,
        email: data.email ?? data.user?.email ?? null,
        first_name: data.first_name ?? data.firstName ?? null,
        last_name: data.last_name ?? data.lastName ?? null,
      };
    }
  } catch (_) {}

  try {
    const { data } = await authApi.get('/whoami', {
      withCredentials: true,
      timeout: 8000,
      validateStatus: (s) => s >= 200 && s < 500,
    });
    if (data?.isAuthenticated) {
      const u = data?.user || {};
      const cid = extractCustomerIdFromWhoami(data);
      if (cid) return { ...u, customer_id: cid };
    }
  } catch (_) {}

  return null;
}

/* -------- Component -------- */
function HrDashboard({ user }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Lokaler User (falls Prop fehlt)
  const [localUser, setLocalUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(false);
  const [userError, setUserError] = useState('');

  // zentrale Projektauswahl
  // ✅ NEU: initial aus globalem HrOverview-Filter (wenn gesetzt)
  const [selectedSurveyId, setSelectedSurveyId] = useState(() => getHrProjectFilterId());
  const [surveyOptions, setSurveyOptions] = useState([]); // [{ id, label }]
  const [loadingSurveys, setLoadingSurveys] = useState(false);
  const [error, setError] = useState('');

  // Sichtbarkeiten
  const [showCurrentSurveys, setShowCurrentSurveys] = useState(true);
  const [showUnregisteredInvitations, setShowUnregisteredInvitations] = useState(true);
  const [showMentoringInvite, setShowMentoringInvite] = useState(true);
  const [showMatchingOverview, setShowMatchingOverview] = useState(true);
  const [showMissingParticipants, setShowMissingParticipants] = useState(true);
  const [showActions, setShowActions] = useState(true);

  // NEU: Kachel-Render erst wenn Survey-Session steht
  const [surveySessionReady, setSurveySessionReady] = useState(false);

  // Effekt: falls Prop fehlt → User nachladen (MENTORING-FIRST)
  useEffect(() => {
    let alive = true;
    (async () => {
      if (user?.customer_id) return; // Prop reicht
      setLoadingUser(true);
      setUserError('');
      try {
        const u = await fetchUserEffective();
        if (!alive) return;
        if (u?.customer_id) {
          setLocalUser(u);
        } else {
          setUserError(t('noCustomerId', 'Kein Kundenkonto gefunden.'));
        }
      } catch (e) {
        if (!alive) return;
        setUserError(t('noCustomerId', 'Kein Kundenkonto gefunden.'));
      } finally {
        if (alive) setLoadingUser(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.customer_id]);

  const effectiveUser = user?.customer_id ? user : localUser;

  /* -------- NEU: Survey-Session-Helper -------- */
  async function ensureSurveySessionOnce() {
    // 1) existiert schon?
    try {
      const who = await fetch('/survey/whoami', { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
      if (who?.hasUser) return;
    } catch {}

    // 2) via Mentoring tenant-package anheften
    try {
      const tp = await fetch('/mentoring/tenant-package', { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
      if (tp?.email && tp?.customer_id) {
        await fetch('/survey/sso-attach', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user: {
              email: tp.email,
              role: 'hr',
              customer_id: tp.customer_id,
              client_id: tp.customer_id,
            },
          }),
        }).catch(() => {});
      }
    } catch {}
  }

  /* -------- NEU: vor Projektwechsel Session sichern -------- */
  useEffect(() => {
    let alive = true;
    setSurveySessionReady(false);

    (async () => {
      if (!selectedSurveyId) {
        if (alive) setSurveySessionReady(true);
        return;
      }
      await ensureSurveySessionOnce();
      if (alive) setSurveySessionReady(true);
    })();

    return () => {
      alive = false;
    };
  }, [selectedSurveyId]);

  /* -------- Surveys laden (Port 3002) -------- */
  async function loadSurveysForCustomer(customerId) {
    // NEU: vor dem ersten Call sicherstellen, dass die Survey-Session steht
    await ensureSurveySessionOnce();

    try {
      const res = await surveyApi.get('/list', {
        params: { customer_id: customerId },
        withCredentials: true,
        validateStatus: (s) => s >= 200 && s < 300,
      });
      return Array.isArray(res.data) ? res.data : [];
    } catch (e) {
      // NEU: einmaliger Retry bei 401/403 nach erneutem Attach
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        await ensureSurveySessionOnce();
        const res2 = await surveyApi.get('/list', {
          params: { customer_id: customerId },
          withCredentials: true,
          validateStatus: (s) => s >= 200 && s < 300,
        });
        return Array.isArray(res2.data) ? res2.data : [];
      }
      throw e;
    }
  }

  function toOptions(items) {
    return items
      .slice()
      .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
      .map((s) => ({
        id: s.survey_id,
        label: `${s.title} (ID: ${s.survey_id})`,
      }));
  }

  async function refreshSurveys() {
    if (!effectiveUser?.customer_id) return;

    // ✅ NEU: globalen Filter als "Wahrheit" lesen
    const globalSurveyId = getHrProjectFilterId();

    setLoadingSurveys(true);
    setError('');
    try {
      const list = await loadSurveysForCustomer(effectiveUser.customer_id);
      let opts = toOptions(list);

      // ✅ NEU: wenn globaler Filter gesetzt → Options strikt einschränken
      if (globalSurveyId) {
        const filtered = opts.filter((o) => Number(o.id) === Number(globalSurveyId));
        if (filtered.length === 0) {
          // globaler Filter zeigt auf ein Projekt, das hier nicht verfügbar ist
          setSurveyOptions([]);
          setSelectedSurveyId(null);
          setError(
            t(
              'surveys.filteredNotFound',
              `Das im HR-Overview ausgewählte Projekt (ID: ${globalSurveyId}) ist hier nicht verfügbar.`
            )
          );
          return;
        }
        opts = filtered;
        setSurveyOptions(opts);
        setSelectedSurveyId(globalSurveyId);
        return;
      }

      // Standardverhalten (kein globaler Filter)
      dbg('surveys', opts);
      setSurveyOptions(opts);
      const stillExists = opts.some((o) => o.id === selectedSurveyId);
      if (!selectedSurveyId || !stillExists) {
        setSelectedSurveyId(opts.length ? opts[0].id : null);
      }
    } catch (e) {
      console.error('Survey list fetch failed:', e);
      setSurveyOptions([]);
      setSelectedSurveyId(null);
      setError(t('surveys.loadError', 'Fehler beim Laden der Umfragen'));
    } finally {
      setLoadingSurveys(false);
    }
  }

  useEffect(() => {
    if (effectiveUser?.customer_id) refreshSurveys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveUser?.customer_id]);

  async function refreshSurveysAfterDelete() {
    await refreshSurveys();
  }

  /* -------- UI Guards -------- */
  if (loadingUser) {
    return <p className="p-4 text-white">{t('loadingUserData', 'Lade Benutzerdaten…')}</p>;
  }

  // WICHTIG: Wenn nicht loading und kein User → klare Fehlermeldung (nicht endloser Spinner)
  if (!loadingUser && !effectiveUser) {
    return (
      <div className="p-4 text-red-500">
        <p>{userError || t('noCustomerId', 'Kein Kundenkonto gefunden.')}</p>
        <button
          onClick={() => navigate('/hr/overview')}
          className="mt-4 bg-mentixxgreen text-black font-bold py-2 px-4 rounded-2xl"
        >
          {t('toHome', 'Zum Dashboard')}
        </button>
      </div>
    );
  }

  if (!effectiveUser?.role || (effectiveUser.role !== 'hr' && effectiveUser.role !== 'mentixx')) {
    return (
      <div className="p-4 text-red-500">
        <p>
          {t('noPermission', { role: effectiveUser?.role }) ||
            `Keine Berechtigung für diese Seite (Rolle: ${effectiveUser?.role}).`}
        </p>
        <button
          onClick={() => navigate('/hr/overview')}
          className="mt-4 bg-mentixxgreen text-black font-bold py-2 px-4 rounded-2xl"
        >
          {t('toHome', 'Zum Dashboard')}
        </button>
      </div>
    );
  }

  if (!effectiveUser.customer_id) {
    return (
      <div className="p-4 text-red-500">
        <p>{t('noCustomerId', 'Kein Kundenkonto gefunden.')}</p>
        <button
          onClick={() => navigate('/hr/overview')}
          className="mt-4 bg-mentixxgreen text-black font-bold py-2 px-4 rounded-2xl"
        >
          {t('toHome', 'Zum Dashboard')}
        </button>
      </div>
    );
  }

  const hrDisplayName = `${effectiveUser?.first_name ?? ''} ${effectiveUser?.last_name ?? ''}`.trim();

  // ✅ NEU: wenn globaler Filter gesetzt, willst du in HrDashboard keine “anderen” Projekte anzeigen

  /* -------- Render -------- */
  return (
    <div className="min-h-screen bg-black text-white p-6">
      {/* Globaler Toaster */}
      <Toaster
        position="top-right"
        gutter={10}
        toastOptions={{
          duration: 3200,
          style: {
            background: '#111827',
            color: '#ffffff',
            border: '1px solid #374151',
            borderRadius: '12px',
            padding: '10px 14px',
          },
          success: { iconTheme: { primary: '#22c55e', secondary: '#111827' } },
          error: { iconTheme: { primary: '#ef4444', secondary: '#111827' } },
        }}
      />

      <div className="flex justify-start mb-6">
        <button
          onClick={() => navigate('/hr/overview')}
          className="bg-green-400 text-black font-semibold px-4 py-2 rounded-full hover:bg-green-300 transition mb-4"
        >
          {t('toHrDashboard', 'Zum HR-Dashboard')}
        </button>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">{t('projectDashboard', 'Projekt-Dashboard')}</h2>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-300 whitespace-nowrap">
            {t('surveys.selectProject', 'Projekt auswählen:')}
          </label>
          <select
            value={selectedSurveyId ?? ''}
            onChange={(e) => {
              const next = e.target.value ? Number(e.target.value) : null;
              setSelectedSurveyId(next);
              setHrProjectFilterId(next);
            }}
            disabled={loadingSurveys}
            className="bg-gray-800 text-white border border-gray-700 rounded-xl px-3 py-2 min-w-[260px]"
          >
            {loadingSurveys && <option value="">{t('loading', 'Laden…')}</option>}
            {!loadingSurveys && surveyOptions.length === 0 && (
              <option value="">{t('surveys.none', 'Keine Umfragen vorhanden')}</option>
            )}
            {!loadingSurveys &&
              surveyOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-gray-800 border border-red-700/60 text-red-200 p-4 rounded-2xl">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-4 mb-8">
        <button
          onClick={() => setShowCurrentSurveys(!showCurrentSurveys)}
          className="px-4 py-2 bg-mentixxgreen text-black rounded-2xl"
        >
          {t('projects', 'Projekte')} {showCurrentSurveys ? t('hide', 'ausblenden') : t('show', 'einblenden')}
        </button>
        <button
          onClick={() => setShowUnregisteredInvitations(!showUnregisteredInvitations)}
          className="px-4 py-2 bg-mentixxgreen text-black rounded-2xl"
        >
          {t('openInvitations', 'Offene Einladungen')}{' '}
          {showUnregisteredInvitations ? t('hide', 'ausblenden') : t('show', 'einblenden')}
        </button>
        <button
          onClick={() => setShowMentoringInvite(!showMentoringInvite)}
          className="px-4 py-2 bg-mentixxgreen text-black rounded-2xl"
        >
          {t('mentoring', 'Mentoring')} {showMentoringInvite ? t('hide', 'verbergen') : t('show', 'einblenden')}
        </button>
        <button
          onClick={() => setShowMatchingOverview(!showMatchingOverview)}
          className="px-4 py-2 bg-mentixxgreen text-black rounded-2xl"
        >
          {t('matchingOverview', 'Matching Überblick')}{' '}
          {showMatchingOverview ? t('hide', 'verbergen') : t('show', 'einblenden')}
        </button>
        <button
          onClick={() => setShowMissingParticipants(!showMissingParticipants)}
          className="px-4 py-2 bg-mentixxgreen text-black rounded-2xl"
        >
          {t('missing.headingShort', 'Handlungsbedarf')}{' '}
          {showMissingParticipants ? t('hide', 'ausblenden') : t('show', 'einblenden')}
        </button>
        <button
          onClick={() => setShowActions(!showActions)}
          className="px-4 py-2 bg-mentixxgreen text-black rounded-2xl"
        >
          Maßnahmen {showActions ? 'ausblenden' : 'einblenden'}
        </button>
      </div>

      {surveySessionReady ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {showCurrentSurveys && (
              <CurrentSurveysTile
                customerId={effectiveUser.customer_id}
                surveyId={selectedSurveyId}
                onSurveyDeleted={refreshSurveysAfterDelete}
              />
            )}
            {showUnregisteredInvitations && (
              <UnregisteredInvitationsTile tenantId={effectiveUser.customer_id} selectedSurveyId={selectedSurveyId} />
            )}
            {showMentoringInvite && (
              <MentoringInviteTile tenantId={effectiveUser.customer_id} selectedSurveyId={selectedSurveyId} />
            )}
          </div>

          {showMissingParticipants && (
            <div className="mt-8">
              {selectedSurveyId ? (
                <MissingParticipants surveyId={selectedSurveyId} hrName={hrDisplayName} hrEmail={effectiveUser?.email} />
              ) : (
                <div className="bg-gray-800 p-6 rounded-2xl text-gray-300">
                  {t('pleaseSelectSurvey', 'Bitte zuerst ein Projekt / eine Umfrage auswählen.')}
                </div>
              )}
            </div>
          )}

          {showMatchingOverview && (
            <div className="mt-8">
              <MatchingTile tenantId={effectiveUser.customer_id} surveyId={selectedSurveyId} />
            </div>
          )}

          <div className="dashboard-grid mt-6">
            <MentoringPairsTile surveyId={selectedSurveyId} />
          </div>

          {showActions && (
            <div className="mt-8">
              <HrActionsTile tenantId={effectiveUser.customer_id} selectedSurveyId={selectedSurveyId} />
            </div>
          )}
        </>
      ) : (
        <div className="text-gray-300">{t('loading', 'Lade…')}</div>
      )}
    </div>
  );
}

export default HrDashboard;
